/**
 * PendingVoucherBanner - Sticky bottom banner for unclaimed vouchers.
 *
 * Displayed site-wide when a user has visited a /claim/:code page
 * but hasn't yet clicked through to claim. Reminds them with a
 * gradient banner at the bottom of the viewport.
 */

import React from 'react';

export interface PendingVoucherBannerProps {
  merchantName: string;
  displayAmount: string;
  claimUrl: string;
  onDismiss: () => void;
}

export const PendingVoucherBanner: React.FC<PendingVoucherBannerProps> = ({
  merchantName,
  displayAmount,
  claimUrl,
  onDismiss,
}) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
      <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Message */}
        <div className="flex items-center gap-2 text-center sm:text-left">
          <svg
            className="w-5 h-5 flex-shrink-0 hidden sm:block"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
            />
          </svg>
          <span className="text-sm sm:text-base font-medium">
            You have <strong>{displayAmount}</strong> from{' '}
            <strong>{merchantName}</strong> to claim!
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={claimUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-white text-blue-700 text-sm font-semibold rounded-full hover:bg-blue-50 transition-colors"
          >
            Claim Now
          </a>
          <button
            onClick={onDismiss}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Dismiss"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
